import React, { useState, useRef, useContext } from "react";
import { useLocation, useNavigate, createSearchParams } from "react-router-dom";
import { getQueryParams, isDropoffStore } from "lib/utils";
import { useTranslation } from "react-i18next";
import QrReader from "components/qr-reader";
import { useUpdateTakeback } from "api/use-takebacks";
import { useTakebackById } from "api/use-takebacks";
import LoadingSpinner from "components/loading-spinner";
import AuthContext from "contexts/AuthContext";

const QrReaderWrapper = ({ isProcessing, setIsProcessing }) => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  const [scannedData, setScannedData] = useState(null);
  const { mutateAsync: updateTakeback } = useUpdateTakeback();
  const takeback = useTakebackById({ id: queryParams.id });
  const hasProcessedRef = useRef(false);
  const handleQrScan = async (data) => {
    if (isProcessing || scannedData === data || hasProcessedRef.current) return;

    setScannedData(data);
    hasProcessedRef.current = true;

    try {
      setIsProcessing(true);

      // Parse the QR code data
      const weightData = JSON.parse(data);

      if (!weightData.signedWeight || !weightData.weight) {
        throw new Error("Invalid QR code - missing weight data");
      }

      // Get original estimated weight from takeback
      const originalEstimatedWeight =
        parseFloat(takeback?.u_lego_presort_weight) ||
        parseFloat(takeback?.rawTakeback?.data?.originalEstimatedWeight) ||
        0;

      // Normalize the weight
      const normalizedWeight = Math.round(weightData.weight * 100) / 100;

      // Update takeback with weight data
      const updatePayload = {
        caseId: takeback?.sys_id || queryParams.id,
        conform_weight: normalizedWeight,
        payment_weight: normalizedWeight,
        uom: "lbs",
        u_lego_weight: normalizedWeight,
        u_lego_uom: "lbs",
        originalEstimatedWeight,
        inStoreVerifiedAt: parseInt(Date.now() / 1000),
        signedWeight: weightData.signedWeight,
        kioskWeightTimestamp: weightData.timestamp,
      };

      await updateTakeback(updatePayload);
      isDropoffStore(user)
        ? navigate({
            pathname: `/en-US/store/trade-in/dropoff`,
            search: createSearchParams(queryParams).toString(),
          })
        : navigate({
            pathname: t("urls.store_trade_in"),
            search: createSearchParams(queryParams).toString(),
          });
    } catch (error) {
      console.error("Error processing weight data:", error);
      setIsProcessing(false);
      alert(`Error scanning the QR code. Please try again`);
    }
  };

  return <QrReader onScan={handleQrScan} bannerText="Scan the kiosk QR Code" />;
};

export default function TradeInScanWeight() {
  const [isProcessing, setIsProcessing] = useState(false);

  if (isProcessing) {
    return <LoadingSpinner isLoading="Processing weight data..." />;
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      <div className="w-full h-full">
        <QrReaderWrapper
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
        />
      </div>
    </div>
  );
}
